import { isSubject } from '../../../subjects.js';
import { classMetadataAgent } from '../class-metadata-agent.js';

export interface ClassMetadataExtraction {
  grade: string | null;
  suffix: string | null;
  /** Canonical {@link Subject} key, or null when no subject was mentioned in the raw text at all. */
  subject: string | null;
}

const EMPTY: ClassMetadataExtraction = { grade: null, suffix: null, subject: null };

/**
 * Splits a free-text class/course label into grade/suffix/subject via {@link classMetadataAgent}.
 * Never throws — any failure (network error, malformed JSON, unrecognized subject key) resolves to
 * {@link EMPTY} so the caller (`linkExamToClassroom` in db/utils/classroom.ts) can fall back to its own
 * plain-regex parser rather than losing the class link entirely.
 */
export async function runClassMetadataAgent(raw: string): Promise<ClassMetadataExtraction> {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed) return EMPTY;

  let rawResult: unknown;
  try {
    rawResult = await new Promise((resolve, reject) => {
      classMetadataAgent(trimmed)().subscribe({ next: resolve, error: reject, complete: () => {} });
    });
  } catch {
    return EMPTY;
  }

  let parsed: any = rawResult;
  if (typeof rawResult === 'string') {
    try {
      parsed = JSON.parse(rawResult);
    } catch {
      return EMPTY;
    }
  }

  const answer = typeof parsed?.answer === 'string' ? parsed.answer : null;
  if (!answer) return EMPTY;

  let data: any;
  try {
    data = JSON.parse(answer);
  } catch {
    return EMPTY;
  }

  const grade = typeof data?.grade === 'string' && data.grade.trim() ? data.grade.trim() : null;
  const suffix = typeof data?.suffix === 'string' && data.suffix.trim() ? data.suffix.trim() : null;
  const subjectCandidate = typeof data?.subject === 'string' ? data.subject.trim().toLowerCase() : '';
  const subject = isSubject(subjectCandidate) ? subjectCandidate : null;

  return { grade, suffix, subject };
}
